// display-file.component.ts
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom } from 'rxjs';
import { TruncatePipe } from 'src/app/pipe/truncate.pipe';
import { FileSizePipe } from 'src/app/pipe/file-size.pipe';
import { authService } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';
import { DialogComponent } from '../dialog/confirm.dialog';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-display-file',
  standalone: true,
  imports: [CommonModule, MatIconModule, TruncatePipe, MatTooltipModule, ReactiveFormsModule, FileSizePipe],
  templateUrl: './display-file.component.html',
  styleUrls: ['./display-file.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DisplayFileComponent),
      multi: true
    }
  ]
})
export class DisplayFileComponent implements OnInit, ControlValueAccessor {
  currentFiles: any[] = [];
  currentFixedFiles: any[] = [];
  imageExpand: boolean = false;
  apiPath: string = environment.apiUrl;
  
  // Add max file size in MB from environment
  maxTotalSizeMB: number = environment.client_max_body_size || 20;
  maxTotalSizeBytes: number = this.maxTotalSizeMB * 1024 * 1024;
  
  // Add max file pieces for SMS
  maxFilePieces: number = environment.client_max_file_pieces || 5;
  
  // Add validation state
  isValid: boolean = true;
  validationError: string = '';

  private _isSms = false;

  @Input() 
  set isSms(value: boolean) {
    if (this._isSms !== value) {
      this._isSms = value;
      this.validateFiles();     // ← re-evaluate limits
    }
  }
  get isSms(): boolean {
    return this._isSms;
  }

  @Input() isAlreadySent: boolean = false;
  @Input() isSendingMessage: boolean = false;
  @Output() removeFileStatic = new EventEmitter<any>();
  @Output() removeFileFromCache = new EventEmitter<string>();

  // For ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(
    private authService: authService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    // Initial calculation to set validation state
    this.validateFiles();
  }

  // ControlValueAccessor methods
  writeValue(value: any): void {
    // No need to handle incoming value, we're just outputting validation state
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
    this.onChange(this.isValid);   //  ←  propagate the current value immediately
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  

  setDisabledState?(isDisabled: boolean): void {
    // Implementation not needed for this use case
  }

  @Input() set attachedFiles(attachedFiles: any) {
    this.currentFiles = Array.isArray(attachedFiles) ? attachedFiles : [];
    this.validateFiles();
  }
  
  @Input() set fixedFiles(fixedFiles: any) {
    this.currentFixedFiles = Array.isArray(fixedFiles) ? fixedFiles : [];
    this.validateFiles();
  }

  // Validate all files (both size and count for SMS)
  validateFiles(): void {
    const previousValid = this.isValid;
    const totalSize = this.calculateTotalSize();
    const totalFileCount = this.currentFiles.length + this.currentFixedFiles.length;
    
    // Check size limit first
    const isSizeValid = totalSize <= this.maxTotalSizeBytes;
    
    // Check file count for SMS
    const isCountValid = !this.isSms || totalFileCount <= this.maxFilePieces;
    
    // Both validations must pass
    this.isValid = isSizeValid && isCountValid;
    
    // Set appropriate error message
    if (!isSizeValid) {
      const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
      this.validationError = `Total file size (${totalSizeMB} MB) exceeds the maximum allowed (${this.maxTotalSizeMB} MB)`;
      this.showError(this.validationError);
    } else if (!isCountValid) {
      this.validationError = `SMS messages are limited to ${this.maxFilePieces} attachments. You currently have ${totalFileCount}.`;
      this.showError(this.validationError);
    } else {
      this.validationError = '';
    }
    
    // Only trigger change detection if the validity changed
    if (previousValid !== this.isValid) {
      this.onChange(this.isValid);
    }
  }
  
  // Calculate total size of all files (now separate from validation)
  calculateTotalSize(): number {
    let totalSize = 0;
    
    // Add sizes from current files
    this.currentFiles.forEach(file => {
      if (file.size) {
        totalSize += file.size;
      }
    });
    
    // Add sizes from fixed files
    this.currentFixedFiles.forEach(file => {
      if (file.size) {
        totalSize += file.size;
      }
    });
    
    return totalSize;
  }
  
  // Show error message
  showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }

  togleImageExpand(): void {
    this.imageExpand = !this.imageExpand;
  }

  async fileClick(key: string): Promise<void> {
    try {
      const newKey = `messages/${key}`;
      const response: any = await firstValueFrom(this.authService.getSignUrl(newKey, '500'));
      window.open(response.signUrl, '_blank');
    } catch (error) {
      console.error('Error opening file:', error);
    }
  }

  getFileIcon(file: any): string {
    const fileName = file.name || '';
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    
    switch (extension) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'xls':
      case 'xlsx':
        return 'table_chart';
      case 'doc':
      case 'docx':
        return 'description';
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'webm':
        return 'audiotrack';
      default:
        return 'insert_drive_file';
    }
  }

  isAudioFile(file: any): boolean {
    const audioTypes = [
      'audio/mp3',
      'audio/wav',
      'audio/ogg',
      'audio/webm',
      'audio/mpeg',
      'audio/vnd.wave',
    ];
    return audioTypes.includes(file.type);
  }

  isImageFile(file: any): boolean {
    const fileName = file.name || '';
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
  }

  isVideoFile(file: any): boolean {
    const videoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
    return videoTypes.some(type => file.type?.includes(type));
  }

  async removeFile(index: number): Promise<void> {
    if (index >= 0 && index < this.currentFiles.length) {
      const file = this.currentFiles[index];
      
      // Immediately remove from array
      this.currentFiles.splice(index, 1);
      
      // Revoke blob URL if it exists
      if (file.url && file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url);
      }
      
      // If file has a cacheKey, emit for cache removal
      if (file.cacheKey) {
        // Emit event to parent component to handle cache removal
        this.removeFileFromCache.emit(file.cacheKey);
      }
      
      this.validateFiles();
    }
  }
  

  async removeSatic(index: number, file: any) {
    if (this.isSendingMessage) {
      if (index >= 0 && index < this.currentFixedFiles.length) {
        this.removeFileStatic.emit(file);
        this.validateFiles(); // Recalculate after removal
      }
    } else {
      if (index >= 0 && index < this.currentFixedFiles.length) {
        const header = 'Are you sure you want to delete this attachment?';
        const content = 'This action will permanently remove the selected attachment.';

        const confirmed = await this.callDialog(header, content);
        
        if(confirmed){
          this.currentFixedFiles.splice(index, 1);
          this.removeFileStatic.emit(file);
          this.validateFiles(); // Recalculate after removal
        }
      }
    }
  }

  callDialog(header: string, content: string) {
    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        header,
        content,
      },
      width: '600px', // Optional: customize dialog size
    });

    return dialogRef.afterClosed().toPromise();
  }
  
  // Get formatted total size for display
  get formattedTotalSize(): string {
    const totalSize = this.calculateTotalSize();
    
    // Format for display
    if (totalSize < 1024) {
      return `${totalSize} B`;
    } else if (totalSize < 1024 * 1024) {
      return `${(totalSize / 1024).toFixed(2)} KB`;
    } else {
      return `${(totalSize / (1024 * 1024)).toFixed(2)} MB`;
    }
  }
  
  // Get percentage of used space
  get usedPercentage(): number {
    const totalSize = this.calculateTotalSize();
    return Math.min(100, (totalSize / this.maxTotalSizeBytes) * 100);
  }
  
  // Get total file count
  get totalFileCount(): number {
    return this.currentFiles.length + this.currentFixedFiles.length;
  }
}